import { useAbTesting } from '@packages/abtesting';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { RecoProductDetailsAbcTestWrapperFragmentFragmentDoc } from '@packages/gql/generated/shopping/RecoProductDetailsAbcTestWrapperFragmentFragmentDoc';
import {
  CriteoProductsRecoSlider,
  PrudsysProductsRecoSlider,
  SeriesProductsRecoSlider,
} from '@packages/recommendations';
import { type FC } from 'react';
import { useIntl } from 'react-intl';
import useSWR from 'swr';
import { useHashParameters } from '../../hooks/useHashParameters';
import type { AbcTestVariants } from '../../types';
import { Recommendations } from '../Recommendations';
import { fallbackRDEHeadlines } from '../Recommendations/messages';

const testId = '2023_09_reco_sequence';

/* GraphQL */ `
  fragment RecoProductDetailsAbcTestWrapperFragment on Product {
    hasSeries
    defaultCategory
    promotion
    orderNumber
    brand {
      name
    }
    availability {
      state
    }
    price {
      value
    }
    ...RecommendationsFragment
  }
`;

export type RecoProductDetailsAbcTestWrapperProps = {
  productData: FragmentType<typeof RecoProductDetailsAbcTestWrapperFragmentFragmentDoc>;
};

/**
 * RecoProductDetailsAbcTestWrapper component description displayed in storybook
 * */
export const RecoProductDetailsAbcTestWrapper: FC<RecoProductDetailsAbcTestWrapperProps> = ({
  productData,
}) => {
  const { category = '' } = useHashParameters();
  const { formatMessage } = useIntl();
  const { setOutcome, setRelevantProduct, getTestRuleset } = useAbTesting();

  const { data, error } = useSWR('recoabctest', () => getTestRuleset(testId));

  const variant = data?.variant.treatment as AbcTestVariants;

  const product = getFragmentData(RecoProductDetailsAbcTestWrapperFragmentFragmentDoc, productData);
  const { orderNumber, brand, defaultCategory, hasSeries, promotion, availability, price } =
    product;

  let recosToRender = null;

  if (error) {
    return <Recommendations recosData={product} />;
  }

  if (variant === 'test1') {
    recosToRender = (
      <PrudsysProductsRecoSlider
        brand={brand?.name ?? ''}
        categoryIds={[defaultCategory ?? category]}
        productId={orderNumber}
        pageTemplate="detailview"
        fetchOnThreshold
        elementIndex={0}
        title={formatMessage(fallbackRDEHeadlines.prudSysFallback)}
        key={`${orderNumber}${0}`}
        onSeen={() => setOutcome(testId, { SEEN: 1 })}
        onProductClick={(akl, sku) => {
          setOutcome(testId, { COF_Prud: 1 });
          setRelevantProduct(testId, sku, 'Prud');
          setRelevantProduct(testId, akl, 'Prud');
        }}
      />
    );
  } else if (variant === 'test2' && hasSeries) {
    recosToRender = (
      <SeriesProductsRecoSlider
        productId={`${orderNumber}${promotion}`}
        elementIndex={1}
        fetchOnThreshold
        pageTemplate="detailview"
        title={formatMessage(fallbackRDEHeadlines.seriesFallback)}
        key={`${orderNumber}${1}`}
        onSeen={() => setOutcome(testId, { SEEN: 1 })}
        onProductClick={(akl, sku) => {
          setOutcome(testId, { COF_Serie: 1 });
          setRelevantProduct(testId, sku, 'Serie');
          setRelevantProduct(testId, akl, 'Serie');
        }}
      />
    );
  } else if (variant !== 'test2') {
    recosToRender = (
      <>
        <PrudsysProductsRecoSlider
          brand={brand?.name ?? ''}
          categoryIds={[defaultCategory ?? category]}
          productId={orderNumber}
          pageTemplate="detailview"
          fetchOnThreshold
          elementIndex={0}
          title={formatMessage(fallbackRDEHeadlines.prudSysFallback)}
          key={`${orderNumber}${0}`}
          onSeen={() => setOutcome(testId, { SEEN: 1 })}
          onProductClick={(akl, sku) => {
            setOutcome(testId, { COF_Prud: 1 });
            setRelevantProduct(testId, sku, 'Prud');
            setRelevantProduct(testId, akl, 'Prud');
          }}
        />
        {hasSeries && (
          <SeriesProductsRecoSlider
            productId={`${orderNumber}${promotion}`}
            elementIndex={1}
            fetchOnThreshold
            pageTemplate="detailview"
            title={formatMessage(fallbackRDEHeadlines.seriesFallback)}
            key={`${orderNumber}${1}`}
            onSeen={() => setOutcome(testId, { SEEN: 1 })}
            onProductClick={(akl, sku) => {
              setOutcome(testId, { COF_Serie: 1 });
              setRelevantProduct(testId, sku, 'Serie');
              setRelevantProduct(testId, akl, 'Serie');
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      {recosToRender}
      <CriteoProductsRecoSlider
        available={availability.state === 'NOT_AVAILABLE' ? 0 : 1}
        price={parseFloat(price.value.toFixed(2))}
        productId={orderNumber}
        fetchOnThreshold
        elementIndex={hasSeries ? 2 : 1}
        pageTemplate="detailview"
        title={formatMessage(fallbackRDEHeadlines.criteoFallback)}
        key={`${orderNumber}${2}`}
        onSeen={() => setOutcome(testId, { SEEN: 1 })}
        onProductClick={(akl, sku) => {
          setOutcome(testId, { COF_SPA: 1 });
          setRelevantProduct(testId, sku, 'SPA');
          setRelevantProduct(testId, akl, 'SPA');
        }}
        brand={brand?.name ?? ''}
      />
    </>
  );
};
